import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import {
  auditsResolver,
  provideAudits,
} from '@kinet-open-network/client/audits/data-access';
import { ABILITY, canGuard } from '@kinet-open-network/client/auth/data-access';
import { AutomationsStore } from '@kinet-open-network/client/automations/data-access';
import { resolveData } from '@kinet-open-network/client/core/api-client';
import { formBuilder, select } from '@kinet-open-network/client/core/forms/ui';
import { DeliveriesStore } from '@kinet-open-network/client/deliveries/data-access';
import { provideDocuments } from '@kinet-open-network/client/documents/data-access';
import {
  BaseFlowsService,
  BaseFlowsStore,
  FlowTypeConfig,
  IDeliverySelectionItem,
  provideFlowTypeConfig,
} from '@kinet-open-network/client/flows/data-access';
import {
  InvoicesOptionsStore,
  InvoicesStore,
} from '@kinet-open-network/client/invoices/data-access';
import { PaymentTypesStore } from '@kinet-open-network/client/payment-types/data-access';
import {
  confirmDialog,
  IBlock,
  toast,
} from '@kinet-open-network/client/shared/ui';
import { DetailAction } from '@kinet-open-network/client/shared/util';
import {
  AbilityInterface,
  buildAddressString,
  createEnumList,
  isInProcessingState,
  IStockItem,
  OrderStatus,
  orderStatusOptions,
} from '@kinet-open-network/shared/data-access/model';
import {
  availableStatusesForType,
  FlowOrderActions,
  flowOrderActionsOptions,
  flowOrderTypeOptions,
  FlowType,
  IFlowOrderSelectionItemData,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { MenuItem } from 'primeng/api';
import { of } from 'rxjs';
import { AppCustomer } from '../../../models/app-customer.model';
import { FlowOrderDetailComponent } from '../feature/detail/flow-orders.detail.component';
import { OrderDeliveryDetailsBlockComponent } from '../feature/detail/order-delivery-details-block.component';
import { FlowOrdersQuickViewComponent } from '../feature/list/flow-orders-quick-view.component';
import { FlowOrders } from './flow-orders.model';
import { FlowOrdersService } from './flow-orders.service';
import { FlowOrdersStore } from './flow-orders.store';

/**
 * Resolves whether the invoice overlay opened from an order should be
 * editable: `edit` when the user can update invoices, `read` otherwise.
 * Read access itself is already enforced by the route's `canGuard`.
 */
const resolveInvoiceAction: ResolveFn<DetailAction> = () =>
  inject(ABILITY)().can('update', 'Invoice') ? 'edit' : 'read';

export const orderFlowConfig: FlowTypeConfig<FlowOrders> = {
  type: FlowType.order,
  label: 'Objednávky',
  icon: 'pi-shopping-cart',
  subject: 'Order',
  visible: (ability) => ability.can('read', 'Order'),
  createComponent: FlowOrderDetailComponent,
  detailComponent: FlowOrderDetailComponent,
  quickViewComponent: FlowOrdersQuickViewComponent,
  providers: [
    PaymentTypesStore,
    FlowOrdersStore,
    { provide: BaseFlowsStore, useExisting: FlowOrdersStore },
    { provide: BaseFlowsService, useExisting: FlowOrdersService },
    FlowOrdersService,
    AutomationsStore,
    DeliveriesStore,
  ],
  labels: {
    createLabel: 'Nová objednávka',
    detailLabel: 'Objednávka',
    listLabel: 'Objednávky',
  },
  createFromCustomerVisible: (customer: AppCustomer) => !customer.isSupplier,
  store: FlowOrdersStore,
  service: FlowOrdersService,
  auditType: 'orders',
  actions: {
    actionOptions: flowOrderActionsOptions,
    actionFields: new Map([
      [
        FlowOrderActions.changeStatus,
        (order?: FlowOrders) =>
          formBuilder([
            select('status', 'Zmeniť stav')
              .options(
                createEnumList(orderStatusOptions).filter((o) =>
                  order?.type
                    ? availableStatusesForType[order?.type]?.includes(
                        o.value as OrderStatus,
                      )
                    : true,
                ),
              )
              .required(),
          ]),
      ],
    ]),
  },
  deliveryConfig: {
    getService: () => inject(FlowOrdersService),
    selectLazyColumns: ['id', 'customer.fullname' as any, 'deliveryAddress'],
    displayLabel: (order: FlowOrders) =>
      `${order?.customer?.fullname} ${buildAddressString(order?.deliveryAddress)} (${order?.id})`,
    forceFilters: `status eq "${OrderStatus.approved}"`,
    canLoad: (order: FlowOrders) => order.status === OrderStatus.planned,
    getSelectionItems: (order: FlowOrders) =>
      order?.items.map(
        (item): IDeliverySelectionItem<IFlowOrderSelectionItemData> => ({
          flowId: order.id,
          requiredAmount: item.quantity,
          name:
            item.stockItem?.name || item.stockItem?.sku || 'Neznáma položka',
          id: item.id,
          groupingKey: `${item.stockItemId}-${item.isFull ?? false}-${item.isSell ?? false}-${item.isComplaint ?? false}`,
          data: {
            isFull: item.isFull || false,
            isSell: item.isSell || false,
            isComplaint: item.isComplaint || false,
            stockItemId: item.stockItemId,
            isService: item.stockItem?.isService || false,
          },
        }),
      ) ?? [],
    getScannedItemData: (item: IStockItem): IFlowOrderSelectionItemData => ({
      stockItemId: item.id,
    }),
    saveSelectionKey: 'toCustomerLoadedItems',
  },
  columns: () => {
    const paymentTypesStore = inject(PaymentTypesStore);
    const paymentTypeOptions = paymentTypesStore.data().map((pt) => ({
      label: pt.name,
      value: pt.id,
    }));

    return [
      { key: 'id', label: 'Číslo', filter: 'text' },
      { key: 'customerId', label: 'Číslo zákazníka', filter: 'text' },
      { key: 'customer.fullname', label: 'Zákazník', filter: 'text' },
      {
        key: 'status',
        label: 'Stav',
        filter: {
          type: 'select',
          options: orderStatusOptions,
          showColor: true,
          showIcon: true,
        },
      },
      {
        key: 'type',
        label: 'Typ objednávky',
        filter: {
          type: 'select',
          options: flowOrderTypeOptions,
          showColor: true,
          showIcon: true,
        },
      },
      {
        key: 'stock.name',
        label: 'Sklad',
        filter: 'text',
      },
      { key: 'deliveryDate', label: 'Dátum dodania', filter: 'date' },
      {
        key: 'paymentType',
        label: 'Typ platby',
        filter: { type: 'select', options: paymentTypeOptions },
      },
      { key: 'total', label: 'Spolu s DPH', filter: 'currency' },
      { key: 'totalBase', label: 'Spolu bez DPH', filter: 'currency' },
      { key: 'totalVat', label: 'DPH spolu', filter: 'currency' },
      { key: 'deletedAt', label: 'Vymazané', filter: 'date', hidden: true },
      { key: 'note', label: 'Poznámka', filter: 'text', hidden: true },
      {
        key: 'externalNumber',
        label: 'Externé číslo',
        filter: 'text',
        hidden: true,
      },
      {
        key: 'extId',
        label: 'ONIX',
        filter: 'text',
        hidden: false,
      },
    ];
  },
  tableConfig: (ability, table) => {
    const router = inject(Router);
    const ordersService = inject(FlowOrdersService);
    const markedCount = table()?.marking.markedCount() ?? 0;
    const markedOrders = () =>
      (table()?.marking.markedRows() ?? []) as FlowOrders[];
    const markedMenuItems: MenuItem[] = [
      {
        label: 'Vytvoriť dodanie',
        icon: 'pi pi-truck',
        visible: ability.can('create', 'Delivery'),
        command: () => {
          // Only draft/approved orders are eligible for a delivery.
          const ids = markedOrders()
            .filter(
              (order) =>
                order.status === OrderStatus.draft ||
                order.status === OrderStatus.approved,
            )
            .map((order) => order.id);
          if (!ids.length) {
            return;
          }
          router.navigate(['/deliveries', 'create'], {
            queryParams: {
              [`flowItems_${FlowType.order}`]: ids.join(','),
            },
          });
        },
      },
      {
        label: 'Vytvoriť faktúru',
        icon: 'pi pi-receipt',
        visible: ability.can('create', 'Invoice'),
        command: () => {
          const availableOrders = markedOrders().filter(
            (order) => !!order.toCustomerUnloadedItems?.length,
          );
          const notAvailableOrders = markedOrders().filter(
            (order) => !order.toCustomerUnloadedItems?.length,
          );
          if (!availableOrders.length) {
            toast({
              severity: 'warn',
              detail: `Objednávky nemajú položky, ktoré boli vyložené u zákazníka`,
            });
            return;
          }
          let message = `Pre každú z ${availableOrders.length} objednávok sa vytvorí samostatná faktúra. \n\n`;

          if (notAvailableOrders.length) {
            message += `<p class="text-danger-600">Objednávky ${notAvailableOrders.map((order) => order.id).join(', ')} nemajú položky, ktoré boli vyložené u zákazníka.</p>`;
          }
          confirmDialog({
            header: 'Vytvoriť faktúry',
            message,
            accept: async () => {
              await ordersService.generateInvoices(
                availableOrders.map((order) => order.id),
              );
              toast({
                severity: 'success',
                detail: 'Faktúry boli vytvorené',
              });
              table()?.marking.unmarkAll();
              table()?.refresh();
            },
          });
        },
      },
    ];
    return {
      deletedToggle: true,
      markable: true,
      header: {
        newButton: {
          label: 'Pridať',
          icon: 'pi pi-plus',
          disabled: ability.cannot('create', 'Order'),
        },
        actions: createEnumList(flowOrderTypeOptions).map((option) => ({
          label: option.label,
          icon: option.icon,
          routerLink: `create`,
          queryParams: {
            type: option.value,
          },
        })),
        extraButtons:
          markedCount > 0 && markedMenuItems.some((item) => item.visible)
            ? [
                {
                  label: `Označené (${markedCount})`,
                  severity: 'success',
                  items: markedMenuItems,
                },
              ]
            : undefined,
      },
    };
  },
  footerBlocks: (model: FlowOrders, ability: AbilityInterface) => {
    const order = model as FlowOrders & {
      recipientName?: string | null;
      recipientEmail?: string | null;
      recipientSignature?: string;
      unloadingStockCode?: string | null;
    };

    if (!isInProcessingState(order.status)) return [];
    const signatureHtml = order?.recipientSignature
      ? `<img class="max-h-28 mt-2" src="data:image/png;base64, ${order.recipientSignature.replace('data:image/png;base64,', '')}" alt="Podpis príjemcu" />`
      : null;

    const blocks: IBlock[] = [
      {
        header: 'Dodanie',
        component: OrderDeliveryDetailsBlockComponent,
        value: {
          recipientName: order?.recipientName ?? null,
          recipientEmail: order?.recipientEmail ?? null,
          deliveryDate: order?.deliveryDate ?? null,
          unloadingStockCode: order?.unloadingStockCode ?? null,
          recipientGpsX: order.recipientGpsX ?? null,
          recipientGpsY: order.recipientGpsY ?? null,
        },
      },
    ];
    if (signatureHtml) {
      blocks.push({
        header: 'Podpis príjemcu',
        value: signatureHtml,
      });
    }
    if (order.invoiceId && ability.can('read', 'Invoice')) {
      blocks.push({
        header: 'Faktúra',
        value: `Vygenerovaná ${order.invoiceId}`,
        buttons: [
          {
            icon: 'pi pi-receipt',
            text: true,
            tooltip: 'Otvoriť faktúru',
            routerLink: ['invoice'],
          },
        ],
      });
    }
    return blocks;
  },
  detailChildrenRoutes: [
    {
      path: '',
      loadComponent: () =>
        import('../feature/detail/tabs/flow-orders-general-tab.component').then(
          (m) => m.FlowOrderGeneralTabComponent,
        ),
      // Nested (not sibling) so FlowOrderGeneralTabComponent stays mounted
      // (rendered dimmed behind the overlay) and hosts these via its own
      // <router-outlet>, matching the pattern used by DeliveryGeneralTabComponent.
      children: [
        {
          path: 'create-delivery',
          data: {
            navigateToListWithIdInParams: true,
            isFlowChildRoute: true,
          },
          loadChildren: () =>
            import('@kinet-open-network/client/deliveries/feature/shell').then(
              (m) => m.createDeliveryRoute(''),
            ),
        },
        {
          path: 'selection',
          canActivate: [canGuard('update', 'Order')],
          loadComponent: () =>
            import('../feature/detail/flow-orders-selection.component').then(
              (m) => m.OrdersSelectionComponent,
            ),
        },
        {
          path: 'edit-step/:status',
          canActivate: [canGuard('update', 'Order')],
          loadComponent: () =>
            import('../feature/detail/flow-orders-selection.component').then(
              (m) => m.OrdersSelectionComponent,
            ),
        },
        {
          path: 'invoice',
          canActivate: [canGuard('read', 'Invoice')],
          providers: [InvoicesStore, InvoicesOptionsStore],
          resolve: {
            model: resolveData(() => {
              const invoiceId = inject(FlowOrdersStore).model()?.invoiceId;
              return invoiceId
                ? inject(InvoicesStore).load(invoiceId)
                : Promise.resolve(undefined);
            }),
            // Opens the invoice read-only unless the user can also edit it.
            action: resolveInvoiceAction,
          },
          loadComponent: () =>
            import('@kinet-open-network/client/invoices/feature/detail').then(
              (m) => m.InvoiceDetailComponent,
            ),
          children: [
            {
              path: '',
              loadComponent: () =>
                import(
                  '@kinet-open-network/client/invoices/feature/detail'
                ).then((m) => m.InvoiceGeneralTabComponent),
            },
            {
              path: 'audits',
              providers: [provideAudits({ type: 'invoices' })],
              resolve: { audits: auditsResolver },
              loadComponent: () =>
                import('@kinet-open-network/client/audits/ui').then(
                  (m) => m.AuditTabComponent,
                ),
            },
          ],
        },
      ],
    },
    {
      path: 'documents',
      title: 'Dokumenty',
      providers: [
        provideDocuments(() => {
          const store = inject(FlowOrdersStore);
          return {
            parentType: 'orders',
            templateContextType: 'order',
            getSendEmailOptions: () => {
              const model = store.model();
              const options = (model?.customer?.contacts ?? [])
                .filter((c): c is typeof c & { email: string } => !!c.email)
                .map((c) => ({
                  label: c.contactName
                    ? `${c.contactName} <${c.email}>`
                    : c.email,
                  value: c.email,
                }));

              return of({ options, default: model?.email ?? undefined });
            },
          };
        }),
      ],
      canActivate: [canGuard('read', 'Document')],
      loadChildren: () =>
        import('@kinet-open-network/client/documents/feature/shell'),
    },
  ],
  createChildrenRoutes: [
    {
      path: '',
      loadComponent: () =>
        import('../feature/detail/tabs/flow-orders-general-tab.component').then(
          (m) => m.FlowOrderGeneralTabComponent,
        ),
    },
  ],
};

export function provideOrderFlowConfig() {
  return provideFlowTypeConfig(orderFlowConfig);
}
